import { safeJsonParse } from '@visiba/std';
import { AbstractSocketEvents, ControllerStates } from './_abstract_socket_events';
import type { ControllerContext } from './_controller_context';
import { ConvertMessageData } from './_convert_message_data';
import type { Disposer, EventHandler } from './_event_emitter';
import { EventEmitter } from './_event_emitter';
import { Reporter } from './_reporter';
import { AxonEvent } from './axon_event';
import type { Message } from './common';
import { HttpConnectionState } from './http_connection';

type Callback<T> = (data: T) => void;
type Invokes = Record<string, Callback<unknown>[]>;

/**
 * ## @visiba/axon
 * Instance that's mapped to websocket message's `C` (controller) value.
 *
 * Communication will only happen within that binding.
 */
export class SocketController extends AbstractSocketEvents<Record<string, unknown> | null | string> {
	private static readonly LOG_TRACE = 'SocketController';

	/**
	 * Stores all invoked functions. Properties are removed when invoked function
	 * is called.
	 */
	private readonly invokes: Invokes = Object.create(null);

	/** Stores all events for this Controller instance. */
	private readonly listener = new EventEmitter();

	constructor(
		private readonly controller: string,
		private readonly controllerContext: ControllerContext,
		private readonly reporter: Reporter,
	) {
		super();

		this.controller = controller;
	}

	/**
	 * Parses a socket message in order to understand what to do with it. If `topic`
	 * is not `2 (open)` or `3 (close)`, it'll fire an event with topic's value.
	 */
	public dispatchEvent(message: Message): void {
		const data = safeJsonParse<string | Record<string, unknown> | null>(message.data);

		switch (message.topic) {
			case AxonEvent.Open: {
				this.state = { state: ControllerStates.Open, data };

				while (this.storedUncalledOnOpenEvents.length > 0) {
					const fn = this.storedUncalledOnOpenEvents.shift()!;
					fn(data);
				}

				this.reporter.log(`Controller "${this.controller}" has been opened`);

				break;
			}
			case AxonEvent.Close:
				this.state = { state: ControllerStates.Closed, data };

				while (this.storedUncalledOnCloseEvents.length > 0) {
					const fn = this.storedUncalledOnCloseEvents.shift()!;
					fn(data);
				}

				this.reporter.log(`Controller "${this.controller}" has been closed`);

				break;
			default: {
				// When you call invoke on a SocketController instance it'll register a
				// one time callback for a topic that's acting more like a regular
				// request rather than a constant stream in backend, this topic will
				// comeback with a concatenated "_r" for the client to act on
				const key = message.topic.replace('_r', '');
				const invoked = this.invokes[key];

				if (invoked && message.topic.includes('_r')) {
					invoked.forEach((fn) => fn(data));

					delete this.invokes[key];
				} else {
					this.listener.emit(message.topic, data, message.id ?? undefined);
				}
			}
		}
	}

	/**
	 * Publishes data to the socket connection with reference to this controller.
	 */
	public publish(topic: AxonEvent | string, data: string | Record<string, unknown> | null = null, id: string | null = null): void {
		const httpConnection = this.controllerContext.httpConnection;
		if (httpConnection.state !== HttpConnectionState.Connected) return;

		const sendData: Message = {
			topic,
			data,
			id,
			controller: this.controller,
		};

		const message = ConvertMessageData.write(sendData);

		httpConnection.send(message);
	}

	/**
	 * Publishes data to the socket connection with a callback that'll be called
	 * once a response socket event is read.
	 */
	public invoke<T>(topic: AxonEvent | string, data: string | Record<string, unknown> | null, callback: Callback<T>): void {
		if (!this.invokes[topic]) this.invokes[topic] = [];

		this.invokes[topic].push(callback as Callback<unknown>);

		this.publish(topic, data);
	}

	/** Adds a listener to an event. */
	public on<T = unknown>(topic: string, event: EventHandler<T>): Disposer {
		return this.listener.on(topic, event);
	}

	/**
	 * Closes this controller. Removes all listeners and instance is removed from
	 * clientReference's Controller list.
	 */
	public close(): void {
		this.listener.offAll();
		this.controllerContext.remove(this.controller);
	}

	/* @internal */
	public communicateReconnect(): void {
		if (this.storedUncalledOnReconnect.length > 0) {
			this.reporter.log(`[${SocketController.LOG_TRACE}] controller "${this.controller}" is reconnecting ...`);
		}

		while (this.storedUncalledOnReconnect.length > 0) {
			const fn = this.storedUncalledOnReconnect.shift()!;
			fn(null);
		}
	}

	/* @internal */
	public communicateReconnected(): void {
		if (this.storedUncalledOnReconnected.length > 0) {
			this.reporter.log(`[${SocketController.LOG_TRACE}] controller "${this.controller}" has reconnected!`);
		}

		while (this.storedUncalledOnReconnected.length > 0) {
			const fn = this.storedUncalledOnReconnected.shift()!;
			fn(null);
		}
	}
}
